import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import {
    Upload, UserPlus, Trash2, Loader2, AlertCircle, CheckCircle2,
    FileSpreadsheet, Hash, Eraser, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";

// ─────────────────────────────────────────────────────────────────────────
// Classroom student registery — management's enrolment record, and the
// roster attendance is graded against.
//
// Deliberately NOT the same thing as ClassroomRoster.tsx next door. That
// one shows who has self-joined the classroom in the app (RDB members) so
// they can receive the timetable and teacher updates. This one is the
// authority-controlled list: a student is enrolled because it says so
// here, not because they tapped Join, and attendance counts every
// registered student whether or not they ever installed the app.
// ─────────────────────────────────────────────────────────────────────────

type RegisteredStudent = {
    student_eid: string;
    student_uid: string | null;
    student_name: string | null;
    added_at: number | null;
};

type RowOutcome = { eid: string; reason: string; classroomId?: string };

type UploadReport = {
    added: string[];
    skipped: RowOutcome[];
    invalid: RowOutcome[];
    duplicates: RowOutcome[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    classRoomId: string | null;
    classroomName: string;
};

// Matches MAX_CLASSROOM_REGISTERY_ROWS on the backend. A larger sheet is
// split into several calls rather than rejected — the cap is there to bound
// one request, not to limit how big an intake can be.
const CHUNK_SIZE = 500;

// Header names an institute might plausibly use for its student number.
const ID_KEYS = [
    "eid", "eid", "employeeid", "empid", "studentid", "id",
    "rollno", "rollnumber", "roll", "rno", "admissionno", "admissionnumber", "admission",
];
const NAME_KEYS = [ "name", "studentname", "fullname" ];

const normalise = (k: string) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");

function formatAddedAt(ts: number | null) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function reasonLabel(reason: string) {
    switch (reason) {
        case "ALREADY_IN_ANOTHER_CLASSROOM": return "Already in another classroom";
        case "ALREADY_IN_THIS_CLASSROOM": return "Already registered here";
        case "UNKNOWN_STUDENT_ID": return "No such student id";
        case "DUPLICATE_IN_UPLOAD": return "Listed twice in the file";
        default: return reason;
    }
}

type ParsedSheet = {
    headers: string[];
    rows: string[][];
    hasHeader: boolean;
    idCol: number | null;
    nameCol: number | null;
};

const columnLetter = (i: number) => String.fromCharCode(65 + (i % 26));

/**
 * Reads a CSV/XLSX into a grid and guesses which columns hold the id and the
 * name. The guess is only a pre-selection — the dialog shows the columns and
 * lets the user pick, because sheets often carry several id-like columns
 * (serial no, roll no, admission id) and the first match is not always the one
 * the institute keys its students by.
 */
function readSheet(buf: ArrayBuffer): ParsedSheet | null {
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[ wb.SheetNames[ 0 ] ];
    if (!ws) return null;

    const raw = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, defval: "" });
    const width = raw.reduce((m, r) => Math.max(m, r?.length ?? 0), 0);
    const grid = raw
        .map(r => Array.from({ length: width }, (_, i) => String(r?.[ i ] ?? "").trim()))
        .filter(r => r.some(c => c));
    if (grid.length === 0 || width === 0) return null;

    const first = grid[ 0 ];
    const idGuess = first.findIndex(h => ID_KEYS.includes(normalise(h)));
    const nameGuess = first.findIndex(h => NAME_KEYS.includes(normalise(h)));
    // A first row with any letters is a header; a bare column of numbers is data.
    const hasHeader = idGuess >= 0 || nameGuess >= 0 || first.some(c => /[a-z]/i.test(c));

    return {
        hasHeader,
        headers: first.map((h, i) => (hasHeader && h) ? h : `Column ${columnLetter(i)}`),
        rows: hasHeader ? grid.slice(1) : grid,
        idCol: idGuess >= 0 ? idGuess : (hasHeader ? null : 0),
        nameCol: nameGuess >= 0 ? nameGuess : (!hasHeader && width > 1 ? 1 : null),
    };
}

/** The {eid, name} rows the current column choice produces. */
function pickRows(sheet: ParsedSheet, idCol: number | null, nameCol: number | null) {
    if (idCol === null) return [];
    return sheet.rows
        .map(r => ({
            eid: r[ idCol ] ?? "",
            name: nameCol !== null ? (r[ nameCol ] || null) : null,
        }))
        .filter(r => r.eid);
}

export function ClassroomRegistery({ open, onClose, classRoomId, classroomName }: Props) {
    const [ students, setStudents ] = useState<RegisteredStudent[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    const [ newEid, setNewEid ] = useState("");
    const [ newName, setNewName ] = useState("");
    const [ adding, setAdding ] = useState(false);

    // Parsed file awaiting confirmation — nothing is sent until the ids have
    // been shown back and accepted.
    const [ pending, setPending ] = useState<{ fileName: string; sheet: ParsedSheet; idCol: number | null; nameCol: number | null } | null>(null);
    const pendingRows = useMemo(
        () => pending ? pickRows(pending.sheet, pending.idCol, pending.nameCol) : [],
        [ pending ],
    );
    const [ uploading, setUploading ] = useState(false);
    const [ uploadProgress, setUploadProgress ] = useState<string | null>(null);
    const [ report, setReport ] = useState<UploadReport | null>(null);

    const [ removingEid, setRemovingEid ] = useState<string | null>(null);
    const [ clearOpen, setClearOpen ] = useState(false);
    const [ clearing, setClearing ] = useState(false);

    const fileRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        if (!classRoomId) return;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ classRoomId });
            const res = await fetch(`${environment.BASE_URL}/classrooms/getClassroomRegistery?${params}`, {
                headers: apiHeaders(),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to load registery");
            setStudents(data.students || []);
        } catch (e: any) {
            setError(e.message || "Failed to load registery");
            setStudents([]);
        } finally {
            setLoading(false);
        }
    }, [ classRoomId ]);

    useEffect(() => {
        if (open) {
            setReport(null);
            setError(null);
            load();
        }
    }, [ open, load ]);

    // Sends one batch and returns its per-row report.
    const postBatch = async (batch: { eid: string; name: string | null }[]): Promise<UploadReport> => {
        const res = await fetch(`${environment.BASE_URL}/classrooms/registerStudents`, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({ classRoomId, students: batch }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "Registration failed");
        return {
            added: data.added || [],
            skipped: data.skipped || [],
            invalid: data.invalid || [],
            duplicates: data.duplicates || [],
        };
    };

    const handleAddOne = async () => {
        const eid = newEid.trim();
        if (!eid || !classRoomId) return;
        setAdding(true);
        setError(null);
        setReport(null);
        try {
            const result = await postBatch([ { eid, name: newName.trim() || null } ]);
            setReport(result);
            if (result.added.length > 0) {
                setNewEid("");
                setNewName("");
            }
            await load();
        } catch (e: any) {
            setError(e.message || "Failed to add student");
        } finally {
            setAdding(false);
        }
    };

    // Parse only — the ids are shown for confirmation before anything is sent.
    const handleFile = async (file: File) => {
        setError(null);
        setReport(null);
        try {
            const sheet = readSheet(await file.arrayBuffer());
            if (!sheet || sheet.rows.length === 0) throw new Error("No rows found in that file");
            setPending({ fileName: file.name, sheet, idCol: sheet.idCol, nameCol: sheet.nameCol });
        } catch (e: any) {
            setError(e.message || "Could not read that file");
        } finally {
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const confirmUpload = async () => {
        if (!classRoomId || !pending || pendingRows.length === 0) return;
        const rows = pendingRows;
        setUploading(true);
        setError(null);
        setUploadProgress(null);
        try {
            // Split into request-sized batches so the backend cap is never hit.
            const merged: UploadReport = { added: [], skipped: [], invalid: [], duplicates: [] };
            const batches = Math.ceil(rows.length / CHUNK_SIZE);
            for (let i = 0; i < batches; i++) {
                if (batches > 1) setUploadProgress(`Batch ${i + 1} of ${batches}…`);
                const part = await postBatch(rows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
                merged.added.push(...part.added);
                merged.skipped.push(...part.skipped);
                merged.invalid.push(...part.invalid);
                merged.duplicates.push(...part.duplicates);
            }
            setReport(merged);
            setPending(null);
            await load();
        } catch (e: any) {
            setError(e.message || "Upload failed");
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const handleRemove = async (eid: string) => {
        if (!classRoomId) return;
        setRemovingEid(eid);
        setError(null);
        try {
            const res = await fetch(`${environment.BASE_URL}/classrooms/unregisterStudent`, {
                method: "POST",
                headers: apiHeaders(),
                body: JSON.stringify({ classRoomId, studentEid: eid }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to remove student");
            await load();
        } catch (e: any) {
            setError(e.message || "Failed to remove student");
        } finally {
            setRemovingEid(null);
        }
    };

    const handleClear = async () => {
        if (!classRoomId) return;
        setClearing(true);
        setError(null);
        try {
            const res = await fetch(`${environment.BASE_URL}/classrooms/clearClassroomRegistery`, {
                method: "POST",
                headers: apiHeaders(),
                body: JSON.stringify({ classRoomId }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to clear registery");
            setReport(null);
            await load();
        } catch (e: any) {
            setError(e.message || "Failed to clear registery");
        } finally {
            setClearing(false);
            setClearOpen(false);
        }
    };

    const notAdded = report
        ? [ ...report.skipped, ...report.invalid, ...report.duplicates ]
        : [];

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
                <DialogContent className="sm:max-w-4xl p-0 gap-0 overflow-hidden">
                    {/* ── Header ─────────────────────────────────────────────── */}
                    <div className="px-6 pt-6 pb-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <DialogTitle className="text-lg font-bold">Student Registery</DialogTitle>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {classroomName || "Classroom"} — enrolment of record. Attendance is taken
                                    against this list, not who has joined the classroom in the app.
                                </p>
                            </div>
                            <Badge variant="outline" className="shrink-0">
                                {students.length} registered
                            </Badge>
                        </div>

                        {/* ── Add one ────────────────────────────────────────── */}
                        <div className="mt-4 flex gap-2">
                            <Input
                                placeholder="Student id (roll / admission / E-ID)…"
                                value={newEid}
                                onChange={(e) => setNewEid(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleAddOne(); }}
                                className="text-xs h-9"
                            />
                            <Input
                                placeholder="Name (optional)"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleAddOne(); }}
                                className="text-xs h-9 max-w-[180px]"
                            />
                            <Button
                                size="sm"
                                className="h-9 gap-1.5 shrink-0"
                                onClick={handleAddOne}
                                disabled={adding || !newEid.trim()}
                            >
                                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                                Add
                            </Button>
                        </div>

                        {/* ── Bulk upload ────────────────────────────────────── */}
                        <div className="mt-2 flex items-center gap-2">
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                className="hidden"
                                onChange={(e) => { const f = e.target.files?.[ 0 ]; if (f) handleFile(f); }}
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 gap-1.5"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                            >
                                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                Upload CSV / Excel
                            </Button>
                            <p className="text-[11px] text-muted-foreground">
                                {uploadProgress
                                    ? uploadProgress
                                    : `A column of student ids, optionally a name column. Sent ${CHUNK_SIZE} at a time.`}
                            </p>
                            <div className="ml-auto">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-9 gap-1.5 text-destructive hover:bg-destructive hover:text-white"
                                    onClick={() => setClearOpen(true)}
                                    disabled={students.length === 0 || clearing}
                                >
                                    <Eraser className="h-3.5 w-3.5" />
                                    Clear
                                </Button>
                            </div>
                        </div>

                        {/* ── Confirm what was read out of the file ─────────── */}
                        {pending && (
                            <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                                <div className="flex items-center gap-2">
                                    <FileSpreadsheet className="h-3.5 w-3.5 text-primary" />
                                    <span className="text-xs font-semibold">
                                        {pending.fileName} — {pending.sheet.rows.length} row{pending.sheet.rows.length === 1 ? "" : "s"}
                                    </span>
                                    <Button
                                        variant="ghost" size="sm"
                                        className="ml-auto h-6 w-6 p-0"
                                        onClick={() => setPending(null)}
                                        disabled={uploading}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>

                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Choose which column holds the student id and which holds the name.
                                    Nothing has been sent yet.
                                </p>

                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <label className="text-[11px] font-medium">
                                        Student id column
                                        <select
                                            className="mt-1 block w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                                            value={pending.idCol ?? ""}
                                            onChange={(e) => setPending({ ...pending, idCol: e.target.value === "" ? null : Number(e.target.value) })}
                                            disabled={uploading}
                                        >
                                            <option value="">Select a column…</option>
                                            {pending.sheet.headers.map((h, i) => (
                                                <option key={i} value={i}>{h}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="text-[11px] font-medium">
                                        Name column (optional)
                                        <select
                                            className="mt-1 block w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                                            value={pending.nameCol ?? ""}
                                            onChange={(e) => setPending({ ...pending, nameCol: e.target.value === "" ? null : Number(e.target.value) })}
                                            disabled={uploading}
                                        >
                                            <option value="">None</option>
                                            {pending.sheet.headers.map((h, i) => (
                                                <option key={i} value={i}>{h}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                {pending.idCol === null ? (
                                    <p className="mt-2 text-[11px] text-amber-700">Pick the student id column to continue.</p>
                                ) : (
                                    <>
                                        <p className="mt-2 text-[11px] text-muted-foreground">
                                            {pendingRows.length} student{pendingRows.length === 1 ? "" : "s"}
                                            {pending.nameCol !== null && ` · ${pendingRows.filter(r => r.name).length} with a name`}
                                            {pending.sheet.rows.length - pendingRows.length > 0 && ` · ${pending.sheet.rows.length - pendingRows.length} blank row(s) skipped`}
                                        </p>
                                        <div className="mt-1 max-h-[140px] overflow-y-auto rounded-md border border-border/60 bg-background">
                                            <table className="w-full text-[11px]">
                                                <tbody>
                                                    {pendingRows.map((r, i) => (
                                                        <tr key={`${r.eid}-${i}`} className="border-b border-border/40 last:border-0">
                                                            <td className="px-2 py-1 font-mono whitespace-nowrap">{r.eid}</td>
                                                            <td className="px-2 py-1 text-muted-foreground">{r.name || "—"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}

                                <div className="mt-3 flex items-center gap-2">
                                    <Button size="sm" className="h-8 gap-1.5" onClick={confirmUpload} disabled={uploading || pendingRows.length === 0}>
                                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                                        Register {pendingRows.length}
                                    </Button>
                                    <Button variant="outline" size="sm" className="h-8" onClick={() => setPending(null)} disabled={uploading}>
                                        Cancel
                                    </Button>
                                    {uploadProgress && (
                                        <span className="text-[11px] text-muted-foreground">{uploadProgress}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mt-3 flex items-start gap-2 text-xs text-red-600">
                                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* ── Per-row outcome of the last write ──────────────── */}
                        {report && (
                            <div className="mt-3 rounded-lg border border-border/60 bg-muted/40 p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-xs font-semibold">
                                        {report.added.length} added
                                    </span>
                                    {notAdded.length > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                            · {notAdded.length} not added
                                        </span>
                                    )}
                                    <Button
                                        variant="ghost" size="sm"
                                        className="ml-auto h-6 w-6 p-0"
                                        onClick={() => setReport(null)}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>

                                {notAdded.length > 0 && (
                                    <div className="mt-2 max-h-[140px] overflow-y-auto space-y-1">
                                        {notAdded.map((row, i) => (
                                            <div key={`${row.eid}-${i}`} className="flex items-center gap-2 text-[11px]">
                                                <Hash className="h-3 w-3 text-muted-foreground shrink-0" />
                                                <span className="font-mono">{row.eid}</span>
                                                <span className="text-muted-foreground">
                                                    {reasonLabel(row.reason)}
                                                    {row.classroomId ? ` (${row.classroomId})` : ""}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <Separator />

                    {/* Registered students are not listed here — this dialog manages
                        enrolment, the Students roster is where you view who is in the
                        classroom (with names resolved from institute records). */}
                    <Separator />
                    <div className="px-6 py-3 flex justify-end">
                        <DialogClose asChild>
                            <Button variant="outline" size="sm">Close</Button>
                        </DialogClose>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Clear confirmation ─────────────────────────────────────────── */}
            <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Clear this classroom's registery?</AlertDialogTitle>
                        <AlertDialogDescription>
                            All {students.length} student(s) will be unregistered from {classroomName || "this classroom"},
                            freeing them to be registered elsewhere. Attendance already recorded is not affected —
                            it stays exactly as it was taken.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Button variant="outline" onClick={() => setClearOpen(false)} disabled={clearing}>Cancel</Button>
                        <Button variant="destructive" onClick={handleClear} disabled={clearing}>
                            {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                            Clear registery
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
